/**
 * Tours específicos para la sección de Manifiestos
 *
 * Estructura de rutas activas:
 *  /manifest-control                     → ManifestControl        (tabla de manifiestos)
 *  /manifest-control/guides              → GuideControl           (gestión de guías individuales)
 *  /manifest-control/guide-dashboard     → GuideDashboard         (dashboard de guías)
 *  /manifest-control/setting/data        → SettingManifest        (configuración)
 */

// ---------------------------------------------------------------------------
// 1. LAYOUT COMPARTIDO — header de ManifestLayout.jsx
//    Prefijo: ML__ (ManifestLayout)
// ---------------------------------------------------------------------------
export const getManifestLayoutTour = (theme) => [
  {
    element: `.ML__${theme}-manifest-layout .ML__header`,
    popover: {
      title: 'Encabezado del módulo',
      description:
        'Barra superior presente en todas las vistas del módulo de Manifiestos. Contiene el título actual, el breadcrumb de navegación y las acciones específicas de cada vista (agregar, refrescar, etc.).',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.ML__${theme}-manifest-layout .ML__header-left`,
    popover: {
      title: 'Título y navegación',
      description:
        'El título cambia según la vista activa: "Control de Manifiestos", "Dashboard de Guías" o "Configuración". El breadcrumb te muestra en qué nivel estás y permite volver a un nivel superior con un clic.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.ML__${theme}-manifest-layout .ML__header-right`,
    popover: {
      title: 'Acciones del header',
      description:
        'Botones de acción específicos de la vista actual. En el Control de Manifiestos aparecen "Agregar Manifiesto" y "Ver Eliminados". En el Dashboard de Guías aparece "Refrescar".',
      side: 'bottom',
      align: 'end',
    },
  },
];

// ---------------------------------------------------------------------------
// 2. CONTROL DE MANIFIESTOS — /manifest-control
//    ManifestControl → ManifestToolbar + ManifestTable
//    Prefijos: MC__ (ManifestControl/ManifestTable), MT__ (ManifestToolbar)
// ---------------------------------------------------------------------------
export const getManifestControlTour = (theme) => [
  ...getManifestLayoutTour(theme),
  {
    element: `.MC__${theme}-manifest-control-container`,
    popover: {
      title: 'Control de Manifiestos',
      description:
        'Vista principal del módulo. Muestra los manifiestos de transporte organizados por sección (BD o BD Urbanos) con filtros, programación de vehículos y acciones de creación/edición en línea.',
      side: 'top',
      align: 'start',
    },
  },

  // ── Toolbar ──────────────────────────────────────────────────────────────
  {
    element: `.MT__${theme}-manifest-toolbar`,
    popover: {
      title: 'Barra de herramientas',
      description:
        'Concentra todos los controles de navegación y filtrado: selector de sección (BD / BD Urbanos), selector de cliente, botones de panel para Programación y Filtros, y el acceso a importación Excel.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.MT__${theme}-manifest-toolbar-section-tabs`,
    popover: {
      title: 'Pestañas de sección',
      description:
        'Cambia entre las dos secciones del módulo: **BD** (Base de Datos — carga líquida de hidrocarburos) y **BD Urbanos** (carga líquida y seca, incluyendo viajes urbanos). Cada sección tiene su propia tabla y configuración.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.MT__${theme}-manifest-toolbar-client-selector`,
    popover: {
      title: 'Selector de cliente (sección BD)',
      description:
        'En la sección BD puedes filtrar por cliente/operación (Ecopetrol, Gran Tierra, Impala, etc.). Solo se muestran las operaciones padre del sistema configuradas en la tabla de operaciones.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: `.MT__${theme}-manifest-toolbar-menus-col`,
    popover: {
      title: 'Paneles expandibles',
      description:
        'Dos paneles opcionales que se despliegan debajo de la barra: **Programación** (vehículos programados para crear manifiestos desde ahí) y **Filtros** (búsqueda por placa, radicado, fecha, etc.). Haz clic para expandir o contraer.',
      side: 'bottom',
      align: 'end',
    },
  },

  // ── Tabla ─────────────────────────────────────────────────────────────────
  {
    element: `.MC__${theme}-manifest-control-content`,
    popover: {
      title: 'Tabla de manifiestos',
      description:
        'Tabla editable en línea con todos los manifiestos filtrados. Puedes hacer clic en cualquier celda para editarla directamente. Los cambios se guardan automáticamente al salir de la celda.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.MC__${theme}-manifest-control-count`,
    popover: {
      title: 'Contador de resultados',
      description:
        'Muestra cuántos manifiestos hay con los filtros actuales vs. el total en la sección. Si hay filtros activos aparece la leyenda "(filtrado de N)".',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: `.ML__action-btn--add`,
    popover: {
      title: 'Agregar manifiesto',
      description:
        'Crea una nueva fila vacía al inicio de la tabla. Completa los campos requeridos y guarda. El número de manifiesto se asigna automáticamente con el siguiente consecutivo disponible.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: `.ML__action-btn--toggle`,
    popover: {
      title: 'Ver manifiestos eliminados',
      description:
        'Activa o desactiva la vista de manifiestos con borrado suave (soft delete). Los manifiestos eliminados no se borran físicamente de la base de datos por razones legales, solo se marcan como inactivos.',
      side: 'bottom',
      align: 'end',
    },
  },
];

// ---------------------------------------------------------------------------
// 3. DASHBOARD DE GUÍAS — /manifest-control/guide-dashboard
//    GuideDashboard → GuideDashboardToolbar + GuideDashboardTable +
//                     GuideDetailPanel + GuideFormModal
//    Prefijos: GD__ (GuideDashboard/GuideDashboardTable), GT__ (GuideDashboardToolbar)
//              GDP__ (GuideDetailPanel), GC__ (GuideFormModal)
// ---------------------------------------------------------------------------

/**
 * Tour interactivo del Dashboard de Guías.
 *
 * Flujo automático:
 *  1. Header del layout (siempre visible)
 *  2. Container + pestañas de asociación
 *  3. Botón filtros → ABRE el panel de filtros (onHighlightStarted)
 *  4. Recorre filtros de fecha, texto, manifiesto y toggles
 *  5. Tabla → CIERRA el panel (onHighlightStarted)
 *  6. Columna Estado + botones de acción
 *  7. ABRE el GuideDetailPanel automáticamente (onHighlightStarted)
 *  8. Recorre header, badges, body del panel
 *  9. Footer del panel: botón en popover → ABRE GuideFormModal (moveTo con delay)
 * 10. Recorre secciones del modal
 * 11. Contador → CIERRA modal y panel (onHighlightStarted)
 *
 * @param {string} theme     — tema activo ('light' | 'dark')
 * @param {object} driverRef — ref con la instancia de driver.js
 */
export const createGuideDashboardTour = (theme, driverRef) => {
  const t = theme;
  const getDriver = () => driverRef.current;

  // ── Helpers DOM ────────────────────────────────────────────────────────

  const openFilterPanel = () => {
    const isOpen = !!document.querySelector(`.GT__${t}-guide-toolbar-panel-body--open`);
    if (!isOpen) document.querySelector(`.GT__${t}-guide-toolbar-panel-tab`)?.click();
  };

  const closeFilterPanel = () => {
    const isOpen = !!document.querySelector(`.GT__${t}-guide-toolbar-panel-body--open`);
    if (isOpen) document.querySelector(`.GT__${t}-guide-toolbar-panel-tab`)?.click();
  };

  const openDetailPanel = () => {
    if (document.querySelector(`.GDP__${t}-guide-detail-panel-overlay`)) return;
    const eyeBtn = [...document.querySelectorAll(`.GD__${t}-guide-dashboard-action-btn`)]
      .find(b => !b.classList.contains(`GD__${t}-guide-dashboard-action-btn--edit`));
    eyeBtn?.click();
  };

  const closeDetailPanel = () => {
    document.querySelector(`.GDP__${t}-guide-detail-panel-close`)?.click();
  };

  const closeEditModal = () => {
    document.querySelector('.GC__modal-footer .GC__btn--secondary')?.click();
  };

  // Handler extraído para evitar anidamiento excesivo en onHighlighted
  const handleOpenFormBtnClick = () => {
    const editBtn = document.querySelector(`.GDP__${t}-guide-detail-panel-btn--edit`);
    if (editBtn) {
      editBtn.click();
      setTimeout(() => getDriver()?.moveTo(MODAL_STEP_IDX), 450);
    } else {
      // Sin guía editable: omitir el bloque del modal y cerrar panel
      closeDetailPanel();
      setTimeout(() => getDriver()?.moveTo(MODAL_STEP_IDX + 4), 200);
    }
  };

  // Índice del paso del modal en el array final (se usa en moveTo)
  const MODAL_STEP_IDX = 17;

  return [
    // ── ManifestLayout — header ──────────────────────────────────────────
    {
      element: '.ML__header',
      popover: {
        title: 'Cabecera del módulo',
        description:
          'Barra superior del módulo de Manifiestos, común a todas sus vistas. ' +
          'Muestra el título de la sección activa, el breadcrumb de navegación y las acciones disponibles.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '.ML__header-left',
      popover: {
        title: 'Título y navegación',
        description:
          'Muestra la vista activa (<em>Dashboard Guías</em>) y el breadcrumb. ' +
          'Haz clic en <strong>Manifiestos</strong> para volver al control principal.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      element: '.ML__action-btn--toggle',
      popover: {
        title: 'Refrescar guías',
        description:
          'Recarga la lista completa de guías desde el servidor sin recargar la página.',
        side: 'bottom',
        align: 'end',
      },
    },

    // ── Dashboard — contenedor ───────────────────────────────────────────
    {
      element: `.GD__${t}-guide-dashboard-container`,
      popover: {
        title: 'Dashboard de Guías',
        description:
          'Vista centralizada de todas las guías del sistema. ' +
          'Cada fila representa una guía vinculada a un manifiesto o programación, ' +
          'ordenadas por fecha de manifiesto (más reciente primero).',
        side: 'top',
        align: 'start',
      },
    },

    // ── Toolbar — pestañas de asociación ─────────────────────────────────
    {
      element: `.GT__${t}-guide-toolbar-association-tabs`,
      popover: {
        title: 'Pestañas de asociación',
        description:
          '• <strong>Todas</strong>: guías activas + manifiestos sin guía.<br>' +
          '• <strong>Con Programación</strong>: guías generadas desde una programación de vehículo.<br>' +
          '• <strong>Con Manifiesto</strong>: guías con manifiesto asignado (incluye manifiestos sin guía).<br>' +
          '• <strong>Independientes</strong>: guías sin manifiesto ni programación.<br>' +
          '• <strong>Sin Guía</strong>: manifiestos sin guía emitida (Anulado, Cancelado, A Espera).',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── Toolbar — abre panel de filtros (onHighlightStarted) ─────────────
    {
      element: `.GT__${t}-guide-toolbar-menus-col`,
      onHighlightStarted: openFilterPanel,
      popover: {
        title: 'Panel de filtros — abriendo',
        description:
          'El panel de filtros se ha abierto automáticamente. ' +
          'El punto naranja indica que hay filtros activos; el botón <strong>✕</strong> los limpia todos a la vez.',
        side: 'bottom',
        align: 'end',
      },
    },

    // ── Filtros — fechas y texto ──────────────────────────────────────────
    {
      element: `.GT__${t}-guide-toolbar-panel-body`,
      popover: {
        title: 'Filtros de fecha y texto',
        description:
          '<strong>F. Manifiesto inicio / fin</strong>: rango enviado al servidor, ' +
          'limita los registros cargados.<br><br>' +
          '<strong>Placa, N.° Guía, N.° Manifiesto, Radicado</strong>: filtran en el ' +
          'cliente sin nueva consulta al servidor.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── Filtros — sección manifiesto ─────────────────────────────────────
    {
      element: `.GT__${t}-guide-toolbar-filter-section`,
      popover: {
        title: 'Filtros de manifiesto',
        description:
          '<strong>Operación</strong>: al seleccionar una operación padre ' +
          'se incluyen sus subdivisiones automáticamente.<br><br>' +
          '<strong>Estado</strong>: muestra solo manifiestos en el estado elegido ' +
          '(Pendiente, Listo, Editado, Cancelado, Anulado o A Espera).',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── Filtros — toggles Cierre / Financiera ─────────────────────────────
    {
      element: `.GT__${t}-guide-toolbar-toggle-group`,
      popover: {
        title: 'Toggles: Cierre y Financiera',
        description:
          '<strong>Cierre</strong>: filtra manifiestos según si el cierre de operación ' +
          'fue aplicado.<br><br>' +
          '<strong>Financiera</strong>: filtra según si la revisión contable está ' +
          'completada (<em>Revisado</em>) o pendiente (<em>Sin Revisar</em>).',
        side: 'top',
        align: 'start',
      },
    },

    // ── Tabla — cierra panel de filtros (onHighlightStarted) ─────────────
    {
      element: `.GD__${t}-guide-dashboard-table-wrapper`,
      onHighlightStarted: closeFilterPanel,
      popover: {
        title: 'Tabla de guías',
        description:
          'Cada fila es una guía o un manifiesto sin guía. ' +
          'Las filas con borde naranja son manifiestos sin guía emitida. ' +
          'Haz clic en una fila con guía para abrir su panel de detalle.',
        side: 'top',
        align: 'start',
      },
    },

    // ── Columna Estado ────────────────────────────────────────────────────
    {
      element: `.GD__${t}-guide-dashboard-cell-estado`,
      popover: {
        title: 'Columna Estado',
        description:
          'El badge de color indica el estado del manifiesto: ' +
          '<em>Pendiente, Listo, Editado, Cancelado, Anulado</em>.<br><br>' +
          'Las letras <strong>C</strong> y <strong>F</strong> indican si el manifiesto ' +
          'tiene <em>Cierre</em> y <em>Revisión Financiera</em> activados.',
        side: 'left',
        align: 'center',
      },
    },

    // ── Acciones ──────────────────────────────────────────────────────────
    {
      element: `.GD__${t}-guide-dashboard-actions`,
      popover: {
        title: 'Botones de acción',
        description:
          '👁 <strong>Ver detalle</strong>: abre el panel lateral con todos ' +
          'los datos de la guía.<br><br>' +
          '✏️ <strong>Editar</strong>: abre el formulario para completar o ' +
          'corregir datos.<br><br>' +
          'Las filas sin guía muestran el badge <em>Sin guía</em> en lugar de botones.',
        side: 'left',
        align: 'center',
      },
    },

    // ── GuideDetailPanel — abre automáticamente (onHighlightStarted) ─────
    {
      element: `.GDP__${t}-guide-detail-panel-panel`,
      onHighlightStarted: openDetailPanel,
      popover: {
        title: 'Panel de detalle — abriendo',
        description:
          'El panel de detalle se ha abierto automáticamente. ' +
          'Muestra todos los datos de la guía. ' +
          'Ciérralo con la X o haciendo clic fuera del panel.',
        side: 'left',
        align: 'start',
      },
    },

    // ── Panel — encabezado ────────────────────────────────────────────────
    {
      element: `.GDP__${t}-guide-detail-panel-header`,
      popover: {
        title: 'Encabezado del panel',
        description:
          'Muestra el número de guía y la placa del vehículo. ' +
          'Si la guía aún no tiene número asignado, aparece como <em>Sin número</em>.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── Panel — insignias ─────────────────────────────────────────────────
    {
      element: `.GDP__${t}-guide-detail-panel-badges`,
      popover: {
        title: 'Insignias de estado',
        description:
          '• <strong>Original</strong>: guía principal del viaje.<br>' +
          '• <strong>Redirección</strong>: el destino cambió; la original queda Invalidada.<br>' +
          '• <strong>Invalidada</strong>: fue reemplazada por una redirección.<br>' +
          '• <strong>Independiente</strong>: sin manifiesto ni programación.<br>' +
          '• <strong>Datos Pendientes</strong>: falta el número de guía.',
        side: 'bottom',
        align: 'start',
      },
    },

    // ── Panel — cuerpo (secciones de datos) ───────────────────────────────
    {
      element: `.GDP__${t}-guide-detail-panel-body`,
      popover: {
        title: 'Datos de la guía',
        description:
          'Organizado en tres secciones:<br>' +
          '• <strong>Información</strong>: N.° guía, placa, cantidad/unidad, ' +
          'vigencia, fechas de salida y expedición.<br>' +
          '• <strong>Asociación</strong>: N.° manifiesto, radicado RNDC, ' +
          'programación vinculada.<br>' +
          '• <strong>Documento adjunto</strong>: previsualización del PDF o imagen.',
        side: 'left',
        align: 'start',
      },
    },

    // ── Panel — footer: botón abre formulario (onHighlighted + moveTo) ────
    // idx 16 — el botón del popover llama a moveTo(MODAL_STEP_IDX = 17)
    {
      element: `.GDP__${t}-guide-detail-panel-footer`,
      onHighlighted: () => {
        setTimeout(() => {
          document
            .getElementById('tour-btn-open-guide-form')
            ?.addEventListener('click', handleOpenFormBtnClick);
        }, 50);
      },
      popover: {
        title: 'Editar guía',
        description:
          '<p style="margin:0 0 12px 0;line-height:1.55">' +
          'El botón <strong>Editar guía</strong> abre el formulario para completar ' +
          'o corregir cualquier dato. ¿Quieres explorarlo?</p>' +
          '<button id="tour-btn-open-guide-form" style="padding:6px 18px;' +
          'background:#3b82f6;color:#fff;border:none;border-radius:6px;' +
          'cursor:pointer;font-size:13px;font-weight:600">Abrir formulario →</button>',
        showButtons: ['close', 'previous', 'next'],
        side: 'top',
        align: 'center',
      },
    },

    // ── GuideFormModal — idx 17 = MODAL_STEP_IDX ─────────────────────────
    {
      element: '.GC__modal',
      popover: {
        title: 'Formulario de guía',
        description:
          'Modal para crear o editar una guía. Al crear, el único campo obligatorio ' +
          'es el archivo adjunto; todos los demás datos pueden completarse después.',
        side: 'top',
        align: 'start',
      },
    },

    // ── Modal — documento adjunto ─────────────────────────────────────────
    {
      element: '.GC__file-section',
      popover: {
        title: 'Documento adjunto',
        description:
          'Arrastra o selecciona el PDF o imagen de la guía. ' +
          'Formatos aceptados: PDF, JPG, PNG, WEBP. Tamaño máximo: 10 MB.<br><br>' +
          'Al editar, si no seleccionas archivo nuevo, se conserva el existente.',
        side: 'right',
        align: 'start',
      },
    },

    // ── Modal — cuerpo (datos opcionales) ────────────────────────────────
    {
      element: '.GC__modal-body',
      popover: {
        title: 'Datos de la guía',
        description:
          'Al <strong>crear</strong> también aparece el selector de tipo: ' +
          '<em>Original</em> (guía principal) o <em>Redirección</em> ' +
          '(reemplaza la original cuando el destino cambia, la invalida automáticamente).<br><br>' +
          'Campos disponibles: N.° de guía, cantidad y unidad del producto, ' +
          'vigencia en horas, fechas de salida, expedición y descargue, y observaciones.',
        side: 'right',
        align: 'start',
      },
    },

    // ── Modal — footer ────────────────────────────────────────────────────
    {
      element: '.GC__modal-footer',
      popover: {
        title: 'Confirmar o cancelar',
        description:
          '<strong>Cancelar</strong>: cierra el formulario sin guardar cambios.<br>' +
          '<strong>Guardar cambios</strong> / <strong>Crear guía</strong>: ' +
          'guarda y actualiza la tabla automáticamente.',
        side: 'top',
        align: 'center',
      },
    },

    // ── Contador — cierra modal y panel (onHighlightStarted) ─────────────
    {
      element: `.GD__${t}-guide-dashboard-count`,
      onHighlightStarted: () => {
        closeEditModal();
        setTimeout(() => closeDetailPanel(), 200);
      },
      popover: {
        title: 'Contador de registros',
        description:
          'Muestra el total de guías visibles con los filtros aplicados. ' +
          'Si hay filtros de texto activos, indica también el total sin filtrar.<br><br>' +
          'El indicador <em>Actualizando…</em> aparece durante una recarga en segundo plano.',
        side: 'top',
        align: 'end',
      },
    },
  ];
};

// ---------------------------------------------------------------------------
// 4. GUÍAS INDIVIDUALES — /manifest-control/guides  (GuideControl)
//    Por ahora tour básico del layout; se puede expandir cuando se documente
//    el componente GuideControl en detalle.
// ---------------------------------------------------------------------------
export const getGuideControlTour = (theme) => [
  ...getManifestLayoutTour(theme),
  {
    element: `.GC__${theme}-guide-control-container`,
    popover: {
      title: 'Control de Guías',
      description:
        'Gestión individual de guías de transporte. Permite ver, crear y editar guías asociadas a una programación o manifiesto específico.',
      side: 'top',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// 5. CONFIGURACIÓN — /manifest-control/setting/data  (SettingManifest)
// ---------------------------------------------------------------------------
export const getManifestSettingTour = (theme) => [
  ...getManifestLayoutTour(theme),
  {
    element: `.ML__${theme}-manifest-layout .ML__content`,
    popover: {
      title: 'Configuración de Manifiestos',
      description:
        'Panel de ajustes del módulo. Aquí se configuran los parámetros maestros necesarios para el correcto funcionamiento del sistema de manifiestos: tasas, productos, rutas frecuentes y otras opciones de precreación.',
      side: 'top',
      align: 'start',
    },
  },
];

// ---------------------------------------------------------------------------
// SELECTOR — elige el tour según la ruta actual de /manifest-control
// ---------------------------------------------------------------------------
export const getManifestTourByPath = (theme, pathname, driverRef) => {
  if (pathname.includes('/manifest-control/guide-dashboard')) return createGuideDashboardTour(theme, driverRef);
  if (pathname.includes('/manifest-control/guides'))          return getGuideControlTour(theme);
  if (pathname.includes('/manifest-control/setting'))         return getManifestSettingTour(theme);
  return getManifestControlTour(theme);
};
